import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core';
import { createStore, ObservableMap } from '@stencil/store';

import type {
  MatchStoreState,
  Match,
  CustomLocale,
  MatchCalendarFormat,
  MatchWording,
} from '../../../services/interfaces';
import { LANGUAGES, MatchUnitSize } from '../../../services/interfaces';
import { loadDateLocale } from '../../../services/libraries';
import { hasSlotContent, isEmpty, isNullUndefined, parseProp } from '../../../services/utils';

import {
  isHorizontalLayout,
  isNational,
  isSizePlus,
  isVerticalLayout,
  setCssVariant,
  setMatchWording,
  printCta,
} from './helpers';
import { MatchUnitHorizontal } from './pk-match-unit.horizontal';
import { MatchUnitVertical } from './pk-match-unit.vertical';

@Component({
  tag: 'pk-match-unit',
  styleUrl: './scss/pk-match-unit.scss',
  shadow: true,
})
export class PkMatchUnit {
  // Own Properties
  // ====================

  private ctaElemRef: HTMLDivElement;

  private highlightCtaElemRef: HTMLDivElement;

  private timeout: NodeJS.Timeout = null;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkMatchUnitElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() _store: ObservableMap<MatchStoreState>;

  // Public Property API
  // ========================

  /** Calendar wording vocabulary  */
  @Prop() dateCalendarFormat: MatchCalendarFormat | string = {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'D MMM',
    lastDay: '[Yesterday]',
    lastWeek: 'D MMM',
    sameElse: 'D MMM',
  };

  /** If true, scorers player link URL won't be available  */
  @Prop({ reflect: true }) disableScorerLink: boolean = false;

  /** If true, home/away team link URL won't be available  */
  @Prop({ reflect: true }) disableTeamsLink: boolean = true;

  /** Data of the match */
  @Prop() matchData: Match | string;

  /** Language to use (defaults to window language) */
  @Prop({ reflect: true }) language: LANGUAGES = LANGUAGES.EN;

  /** Custom dayjs locale */
  @Prop() locale: CustomLocale | string;

  /** Base link of the player service used on the player scorer link.
   * Please, keep in mind the placeholder as they are, no matter if they change position in the string */
  @Prop({ reflect: true }) playerServiceUrl: string =
    'https://www.uefa.com/api/v1/linkrules/player/{{playerId}}/?competitionId={{competitionId}}';

  /** Size to apply to the component */
  @Prop({ reflect: true }) size: MatchUnitSize = MatchUnitSize.M;

  /** Removes the full match date */
  @Prop({ reflect: true }) hideDate: boolean;

  /** Removes the match round name */
  @Prop({ reflect: true }) hideRound: boolean;

  /** Removes the match context: group, league, 1st leg result, aggregate, reason win */
  @Prop({ reflect: true }) hideContext: boolean;

  /** Removes the internal CTA */
  @Prop({ reflect: true }) hideCta: boolean;

  /** Shows venue and city */
  @Prop({ reflect: true }) showLocation: boolean;

  /** Shows the match season */
  @Prop({ reflect: true }) showSeason: boolean;

  /** Shows the match red cards */
  @Prop({ reflect: true }) showRedCards: boolean;

  /** Shows the match fouls counter */
  @Prop({ reflect: true }) showFouls: boolean;

  /** Specify the CTA aria label text for accessibility */
  @Prop({ reflect: true }) ctaAriaLabel: string | null;

  /** Base link of the team service url used on the home/away team link.
   * Please, keep in mind the placeholder as they are, no matter if they change position in the string */
  @Prop({ reflect: true }) teamServiceUrl: string =
    'https://www.uefa.com/api/v1/linkrules/team/{{teamId}}/?competitionId={{competitionId}}';

  /** Match wording vocabulary */
  @Prop() translations: MatchWording | string = {
    '1stleg': '1st leg',
    'line-ups': 'Line-ups',
    'single-leg-match': 'Single-leg fixture',
    abandoned: 'Match abandoned',
    aggregate_abbr: 'Agg',
    canceled: 'Match cancelled',
    extra_time_short: 'ET {minute}',
    full_time: 'Full time',
    ft: 'FT',
    live: 'Live',
    watch_live: 'Watch live',
    mu_goal: 'Goal!',
    owngoal_abbr: 'og',
    penalty_abbr: 'p',
    postponed: 'Match postponed',
    suspended: 'Match suspended',
    futsal_foulcounter: 'Foul count',
    tbc: 'TBC',
    see_more: 'See more',
    view_details: 'View details',
    watch: 'Watch',
  };

  /** If `true`, the `displayTeamCode` will be used on the badge team's name instead */
  @Prop({ reflect: true }) useTeamCode: boolean = false;

  /** CustomTimeZone dayjs timezone plugin */
  @Prop() customTimeZone: string;

  /** Change cta slot to occupy the whole div */
  @Prop() showHighlightUrl: boolean = false;

  /** Timeout in seconds to hide the goal indicator (default: will reset with the next polling) */
  @Prop({ reflect: true }) goalIndicatorTimeout: number;

  /** If true, it means the match is displayed in the brackets section */
  @Prop({ reflect: true }) shownInBrackets: boolean = false;

  // Prop lifecycle events
  // =======================

  @Watch('matchData')
  parseMatchDataProp() {
    if (!this.matchData) return;

    const match = parseProp(this.matchData);

    this.handleGoalChange(this._store.state.matchData, match);

    this._store.state.matchData = match;
  }

  @Watch('dateCalendarFormat')
  parseDateCalendarFormat() {
    const dateCalendarFormat = parseProp(this.dateCalendarFormat);
    this._store.state.dateCalendarFormat = dateCalendarFormat;
  }

  @Watch('translations')
  parseTranslationsObject() {
    if (!this.translations || isEmpty(this.translations)) {
      setMatchWording(this._store.state.wording);
      return;
    }

    const wording = parseProp(this.translations);
    this._store.state.wording = wording;

    setMatchWording(this._store.state.wording);
  }

  @Watch('locale')
  @Watch('language')
  handleLocaleChange() {
    this._store.state.language = this.language;

    if (!this.locale) {
      loadDateLocale(this.language?.toLowerCase());
      this._store.state.locale = this.language?.toLowerCase();
      return;
    }

    const locale = parseProp(this.locale);
    loadDateLocale(locale.name, locale);

    this._store.state.locale = locale;
  }

  @Watch('disableScorerLink')
  @Watch('disableTeamsLink')
  @Watch('hideCta')
  @Watch('hideDate')
  @Watch('hideRound')
  @Watch('hideContext')
  @Watch('showSeason')
  @Watch('showLocation')
  @Watch('showRedCards')
  @Watch('showFouls')
  @Watch('language')
  @Watch('playerServiceUrl')
  @Watch('size')
  @Watch('teamServiceUrl')
  @Watch('customTimeZone')
  @Watch('showHighlightUrl')
  @Watch('ctaAriaLabel')
  @Watch('shownInBrackets')
  handlePropsChange() {
    const hideDate = isNullUndefined(this.hideDate)
      ? this.size === MatchUnitSize.M || this.size === MatchUnitSize.S_PLUS || this.size === MatchUnitSize.L_PLUS
      : this.hideDate;
    const hideRound = isNullUndefined(this.hideRound) ? isVerticalLayout(this.size) : this.hideRound;
    const showRedCards = this.showRedCards;
    const showFouls = this.showFouls;

    this._store.state.disableScorerLink = this.disableScorerLink;
    this._store.state.disableTeamsLink = this.disableTeamsLink;
    this._store.state.hideCta = this.hideCta;
    this._store.state.hideDate = hideDate;
    this._store.state.hideRound = hideRound;
    this._store.state.hideContext = this.hideContext;
    this._store.state.showSeason = this.showSeason;
    this._store.state.showRedCards = showRedCards;
    this._store.state.showFouls = showFouls;
    this._store.state.playerServiceUrl = this.playerServiceUrl;
    this._store.state.teamServiceUrl = this.teamServiceUrl;
    this._store.state.size = this.size;
    this._store.state.customTimeZone = this.customTimeZone;
    this._store.state.showHighlightUrl = this.showHighlightUrl;
    this._store.state.showLocation = this.showLocation;
    this._store.state.ctaAriaLabel = this.ctaAriaLabel;
    this._store.state.shownInBrackets = this.shownInBrackets;
  }

  @Watch('size')
  @Watch('useTeamCode')
  handleUseTeamCodeChange() {
    const matchData = this._store.state.matchData;
    const useTeamCode = isNullUndefined(this.useTeamCode)
      ? isNational(null, matchData?.competition?.teamCategory) && this.size === MatchUnitSize.S
      : this.useTeamCode;

    this._store.state.useTeamCode = useTeamCode;
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the CTA is clicked */
  @Event() pkCtaClick: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  connectedCallback() {
    this._store = createStore(INIT_STATE);

    this.handleLocaleChange();
    this.parseMatchDataProp();
    this.parseDateCalendarFormat();
    this.parseTranslationsObject();
    this.handlePropsChange();
    this.handleUseTeamCodeChange();
  }

  componentDidLoad() {
    this.handleSlotChange();
    this.handleVideoSlotChange();
  }

  disconnectedCallback() {
    this._store.reset();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleGoalChange = (currentMatch: Match, newMatch: Match) => {
    if (!currentMatch || !newMatch) return;

    const homeGoalsCurrent = currentMatch.score?.total?.home ?? 0;
    const awayGoalsCurrent = currentMatch.score?.total?.away ?? 0;
    const homeGoalsNew = newMatch.score?.total?.home ?? 0;
    const awayGoalsNew = newMatch.score?.total?.away ?? 0;

    const goalScored = homeGoalsNew > homeGoalsCurrent || awayGoalsNew > awayGoalsCurrent;

    if (!this.goalIndicatorTimeout) {
      this._store.state.goalScored = goalScored;
      return;
    }

    if (!goalScored) return;

    this._store.state.goalScored = true;

    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this._store.state.goalScored = false;
    }, this.goalIndicatorTimeout * 1000);
  };

  private handleSlotChange = () => {
    if (!this.ctaElemRef) return;

    this._store.state.isCtaSlotEmpty = !hasSlotContent(this.ctaElemRef, 'cta');
  };

  private handleVideoSlotChange = () => {
    if (!this.highlightCtaElemRef) return;

    this._store.state.isHighlightCtaSlotEmpty = !hasSlotContent(this.highlightCtaElemRef, 'highlight-cta');
  };

  private renderCtaElement = () => {
    const cssVariant = setCssVariant(this._store.state.size);
    const isCtaSlotEmpty = this._store.state.isCtaSlotEmpty;
    const match = this._store.state.matchData;

    const showCta = printCta(match);

    return (
      <div
        class={{
          'pk-match__cta-slot': true,
          'is-empty': isCtaSlotEmpty,
          hidden: !showCta,
          ...cssVariant,
        }}
        part="cta"
        ref={(el: HTMLDivElement) => (this.ctaElemRef = el)}
      >
        <slot name="cta" onSlotchange={this.handleSlotChange} />
      </div>
    );
  };

  private renderHighlightCtaElement = () => {
    const cssVariant = setCssVariant(this._store.state.size);
    const isHighlightCtaSlotEmpty = this._store.state.isHighlightCtaSlotEmpty;

    const match = this._store.state.matchData;
    const showHlCta = printCta(match);

    if (!this.showHighlightUrl) return null;

    return (
      <div
        class={{
          'pk-match__highlight-cta': true,
          'is-empty': isHighlightCtaSlotEmpty,
          hidden: !showHlCta,
          ...cssVariant,
        }}
        part="highlight-cta"
        ref={(el: HTMLDivElement) => (this.highlightCtaElemRef = el)}
      >
        <slot name="highlight-cta" onSlotchange={this.handleVideoSlotChange} />
      </div>
    );
  };

  private onCtaClick = (e: MouseEvent) => {
    e.stopPropagation();
    // Enter and Space key are handled by default in the same way as left click button (e.button === 0)
    const allowedMouseButtons = [0, 1];
    if (!allowedMouseButtons.includes(e.button)) return;

    this.pkCtaClick.emit();
  };

  // render() function
  // Always the last one in the class.
  // ===================================
  render() {
    const size = this._store.state.size;
    const cssVariant = setCssVariant(size);
    const isVertical = isVerticalLayout(size);
    const isHorizontal = isHorizontalLayout(size);
    const isPlusSize = isSizePlus(size);
    const isSizeAndHighlightUrl = isPlusSize && this.showHighlightUrl;

    if (!this._store.state.matchData) return <Host />;

    return (
      <div
        class={{
          'pk-match-unit': true,
          ...cssVariant,
        }}
        part="container"
      >
        {isVertical && (
          <MatchUnitVertical state={this._store.state} onCtaClick={this.onCtaClick}>
            {isSizeAndHighlightUrl && this.renderHighlightCtaElement()}
            {this.renderCtaElement()}
          </MatchUnitVertical>
        )}
        {isHorizontal && (
          <MatchUnitHorizontal state={this._store.state} onCtaClick={this.onCtaClick}>
            {this.renderCtaElement()}
          </MatchUnitHorizontal>
        )}
      </div>
    );
  }
}

const INIT_STATE: MatchStoreState = {
  dateCalendarFormat: {},
  disableScorerLink: false,
  disableTeamsLink: true,
  hideCta: false,
  isCtaSlotEmpty: true,
  isHighlightCtaSlotEmpty: true,
  language: null,
  locale: null,
  matchData: null,
  playerServiceUrl: null,
  size: null,
  teamServiceUrl: null,
  wording: {},
  hideDate: false,
  hideRound: false,
  hideContext: false,
  shownInBrackets: false,
  showRedCards: false,
  showSeason: false,
  showLocation: false,
  showFouls: false,
  useTeamCode: false,
  showHighlightUrl: false,
  ctaAriaLabel: null,
};
